import { Immutable } from "immer";
import React from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { LandingPromoProductBlock as Value } from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from
// `cms/landing_page.html` (the `block_type == 'promo_product_block'` branch)
// verbatim — every classname is load-bearing for the existing SCSS, so keep
// them in sync.
//
// LANDING vs HOME differences (the HOME variant lives in
// `../PromoProductBlock`):
//   * `image` here is a LIST (`ImageChooserBlock[]`), not a single image. The
//     image-container wrapper always renders; each image becomes its own
//     `landing-page-promo__image` background div.
//   * `title` AND `description` are RichTextBlocks (HTML) → render via
//     dangerouslySetInnerHTML (HOME's `description` was plain CharBlock text).
//   * the CTA button is just `button button--ming landing-page-promo__button`
//     — there is NO `al-*` analytics class on this landing variant (HOME had
//     `al-hp-promo-module__button`). Confirmed against the legacy source.
//
// Field handling: `pre_title` and `link_name` are plain CharBlocks → render as
// escaped text children to match Django autoescape.
type IProps = Immutable<{
    value: Value;
}>;

type Image = NonNullable<Value["image"]>[number];

const backgroundImageStyle = (
    image: Immutable<Image>,
): React.CSSProperties | undefined => {
    // Legacy emitted `style="background-image: url(<url>);"` (no quotes). When
    // the image is null we omit the inline style entirely while keeping the
    // wrapper element. See the HOME PromoProductBlock / LifestyleModule
    // precedent.
    if (!image) {
        return undefined;
    }
    return { backgroundImage: `url(${image.url})` };
};

export const LandingPromoProductBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const images = v.image ?? [];
    // watchCSS gates the carousel to the mobile breakpoint.
    const imageContainerRef = React.useRef<HTMLDivElement>(null);
    useFlickity(imageContainerRef, {
        module: "flickity",
        options: { watchCSS: true, prevNextButtons: false },
    });

    return (
        <div className="l-full-width landing-page-promo landing-page__section">
            <div
                className="landing-page-promo__image-container"
                ref={imageContainerRef}
            >
                {images.map((image, index) => (
                    <div
                        key={index}
                        className="landing-page-promo__image"
                        style={backgroundImageStyle(image)}
                    ></div>
                ))}
            </div>

            <div className="landing-page-promo__copy landing-page-promo--height">
                <div className="landing-page-promo__copy-inner">
                    <div className="landing-page-promo__pre-title">
                        {v.pre_title ?? ""}
                    </div>
                    <div
                        className="landing-page-promo__title landing-page__title"
                        dangerouslySetInnerHTML={{ __html: v.title ?? "" }}
                    />
                    <div
                        className="landing-page-promo__desc landing-page__desc"
                        dangerouslySetInnerHTML={{
                            __html: v.description ?? "",
                        }}
                    />
                    <a
                        className="button button--ming landing-page-promo__button"
                        href={v.link?.url ?? ""}
                    >
                        <span>{v.link_name ?? ""}</span>
                    </a>
                </div>
            </div>
        </div>
    );
};
